import * as React from 'react'
import type { ColumnDef } from '@tanstack/react-table'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { TextWithTooltip } from '@/components/custom/text-with-tooltip'
import type { AppointmentRecord } from '@/features/appointments/types'
import { parseAppointmentDate } from '@/features/appointments/utils/parseAppointmentDate'
import { AppointmentStatusBadge } from './AppointmentStatusBadge'

const Unavailable = () => <span className="text-muted-foreground">-</span>

/**
 * Format a `date`-typed value (YYYY-MM-DD) or ISO timestamp as a local
 * calendar date (see `parseAppointmentDate` for the timezone/overflow rules).
 */
export function formatAppointmentDate(value: string | null): string | null {
  const date = parseAppointmentDate(value)
  if (!date) return null
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/** Format an ISO timestamp as a local wall-clock time, e.g. "9:00 AM". */
export function formatAppointmentTime(value: string | null): string | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/** At-a-glance columns for the Appointments v3 table. */
export const appointmentsColumns: ColumnDef<AppointmentRecord>[] = [
  {
    accessorKey: 'source_appointment_date',
    enableHiding: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Appointment Date" />
    ),
    cell: ({ row }) => {
      const formatted = formatAppointmentDate(
        row.original.source_appointment_date
      )
      if (!formatted) return <Unavailable />
      return <div className="whitespace-nowrap">{formatted}</div>
    },
  },
  {
    accessorKey: 'start_time',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Appointment Time" />
    ),
    cell: ({ row }) => {
      const formatted = formatAppointmentTime(row.original.start_time)
      if (!formatted) return <Unavailable />
      return <div className="whitespace-nowrap">{formatted}</div>
    },
  },
  {
    accessorKey: 'appointment_type',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Appointment Type" />
    ),
    cell: ({ row }) => {
      const value = row.original.appointment_type
      if (!value) return <Unavailable />
      return <TextWithTooltip text={value} className="max-w-[220px]" />
    },
  },
  {
    // The Location filter (compact checkbox, keyed on `facility_id`) resolves
    // its column via `table.getColumn('facility_id')` — without a column of
    // this id, TanStack logs "Column with id 'facility_id' does not exist"
    // and the filter's checkboxes silently no-op. There's no joined facility
    // name on the appointment row (relay only joins facility_id), so this
    // displays the raw `location` text instead.
    //
    // enableSorting: false — the column id (and therefore what the table
    // sorts by) must stay `facility_id` for the filter lookup above, but the
    // cell renders `location` text; sorting by the underlying uuid would
    // make the visible Location text look randomly ordered.
    accessorKey: 'facility_id',
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Location" />
    ),
    cell: ({ row }) => {
      const value = row.original.location
      if (!value) return <Unavailable />
      return <TextWithTooltip text={value} className="max-w-[220px]" />
    },
  },
  {
    accessorKey: 'patient_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Patient Name" />
    ),
    cell: ({ row }) => {
      const value = row.original.patient_name
      if (!value) return <Unavailable />
      return <TextWithTooltip text={value} className="max-w-[220px]" />
    },
  },
  {
    // Same reasoning as the `facility_id` column above, for the Provider
    // filter (keyed on `provider_id`). Displays the joined `provider_name`.
    // enableSorting: false for the same reason — sorts by uuid, not name.
    accessorKey: 'provider_id',
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Provider" />
    ),
    cell: ({ row }) => {
      const value = row.original.provider_name
      if (!value) return <Unavailable />
      return <TextWithTooltip text={value} className="max-w-[220px]" />
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const value = row.original.status
      if (!value) return <Unavailable />
      return <AppointmentStatusBadge status={value} />
    },
  },
  {
    id: 'resource_name',
    accessorFn: (row) => {
      const value = row.additional_info?.['resource_name']
      return typeof value === 'string' ? value : ''
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Resource Name" />
    ),
    cell: ({ getValue }) => {
      const value = getValue<string>()
      if (!value) return <Unavailable />
      return <TextWithTooltip text={value} className="max-w-[220px]" />
    },
  },
]

export function sourceDisplayName(source: string | null): string | null {
  return source === 'careforce' ? 'Careforce' : source
}
